import axios from 'axios';
import {useStore} from './store';
import {useModalBus} from '@/core/composables/useModalBus';

const csrfToken = document.querySelector('meta[name=\'csrf-token\']')?.getAttribute('content');

const instance = axios.create({
    withCredentials: true,
    headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});

function toggleLoader() {
    const {store} = useStore();
    store.value.isRequestPending = !store.value.isRequestPending;
}

instance.interceptors.request.use(async function(config) {
    const {store} = useStore();
    const baseUrl = await waitBaseUrl();
    
    config.headers['X-Redmine-API-Key'] = store.userData?.api_key || null;
    config.url = `${baseUrl.replace(/\/$/, '')}/${config.url.replace(/^\//, '')}`;
    toggleLoader();

    return config;
}, function(error) {
    toggleLoader();

    return Promise.reject(error);
});

instance.interceptors.response.use(function(response) {
    toggleLoader();

    return response;
}, function(error) {
    toggleLoader();

    const {createAlert} = useModalBus();
    const {response} = error;
    const errors = response ? (response.data).errors : 'Network error';
    
    let message = '';

    if (response && Array.isArray(errors)) {
        message = errors.join('<br>');
    } else {
        message = errors;
    }

    createAlert({
        title: 'Error',
        message,
        status: 'error',
    });

    return Promise.reject(error);
});

function waitBaseUrl(timeout = 200) {
    return new Promise((resolve) => {
        const checkInterval = 10;
        let waited = 0;
  
        const interval = setInterval(() => {
            if (window.VITE_RELATIVE_URL_ROOT) {
                clearInterval(interval);
                resolve(window.VITE_RELATIVE_URL_ROOT);
            } else if (waited >= timeout) {
                clearInterval(interval);
                resolve('');
            } else {
                waited += checkInterval;
            }
        }, checkInterval);
    });
}

export const useAxiosPlugin = () => {
    function httpGet(url, queryParams) {
        return instance.get(url, {params: queryParams});
    }

    function httpPost(url, data, config) {
        const configHeaders = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (data instanceof FormData) {
            configHeaders.headers['Content-Type'] = 'multipart/form-data';
        }

        return instance.post(url, data, config || configHeaders);
    }

    function httpPut(url, data, config) {
        const configHeaders = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (data instanceof FormData) {
            configHeaders.headers['Content-Type'] = 'multipart/form-data';
        }

        return instance.put(url, data, config || configHeaders);
    }

    function httpPatch(url, data, config) {
        const configHeaders = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (data instanceof FormData) {
            configHeaders.headers['Content-Type'] = 'multipart/form-data';
        }

        return instance.patch(url, data, config || configHeaders);
    }

    function httpDelete(url) {
        return instance.delete(url);
    }

    return {
        httpGet,
        httpPost,
        httpPut,
        httpPatch,
        httpDelete,
    };
};
